import React from 'react'
import { connect } from 'react-redux'

import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import { numberWithCommas } from '@utilities/helper'

export interface Property {
  id: string
  name: string
  price: number
  status: string
  canAction: boolean
  hidePrice?: boolean
  selected: boolean
}

type PropertiesListProps = {
  projectIdentity: ProjectIdentity
  className?: string
  data: Property[]
  onClick: (id: string) => void
  labelPrefix?: string
}

function PropertiesList({
  projectIdentity,
  data,
  onClick,
  className = '',
  labelPrefix = '',
}: PropertiesListProps) {
  const { disableSoldProperty, statusLabels, showPrice, hideSoldPrice } =
    projectIdentity
  const availableStatusLabel = statusLabels?.available

  const handleClick = React.useCallback(
    (property: Property) => () => {
      if (!property.canAction) {
        return
      }
      onClick(property.id)
    },
    [onClick]
  )

  const checkDisableStatus = React.useCallback(
    (item: Property) => {
      const isSold = item.status !== availableStatusLabel
      if (isSold && disableSoldProperty) {
        return true
      }
      return false
    },
    [availableStatusLabel, disableSoldProperty]
  )

  const checkShowPriceStatus = React.useCallback(
    (item: Property) => {
      const status =
        item.price > 0 &&
        !item.hidePrice &&
        showPrice &&
        !(item.status !== availableStatusLabel && hideSoldPrice)
      return status
    },
    [availableStatusLabel, showPrice, hideSoldPrice]
  )

  return (
    <div className={`w-full ${className}`}>
      <div className="custom-scrollbar flex h-full flex-col gap-[15px] overflow-y-auto">
        {data.map((item) => (
          <button
            key={item.id}
            className={`rounded-lg bg-gradient text-neutralColour ${item.canAction && !checkDisableStatus(item) ? 'cursor-pointer' : 'cursor-not-allowed'} ${checkDisableStatus(item) ? 'opacity-60' : ''}`}
            onClick={handleClick(item)}
            type="button"
            disabled={checkDisableStatus(item)}
          >
            <div className="flex h-full w-full flex-col gap-3 px-8 py-6">
              <div className="flex w-full items-center justify-between">
                <p className="m-0 text-[41px] font-medium leading-none">
                  {`${labelPrefix}${item.name}`}
                </p>
                <div className="flex h-[25px] w-[25px] items-center justify-center rounded-md bg-neutralColour">
                  {item.selected ? (
                    <div className="h-[17px] w-[17px] rounded-md bg-gradient" />
                  ) : null}
                </div>
              </div>
              <div className="flex w-full items-center justify-between">
                {checkShowPriceStatus(item) ? (
                  <p className="m-0 text-[25px] leading-none">{`$${numberWithCommas(String(item.price))}`}</p>
                ) : null}
                <p className="m-0 ml-auto text-right text-[25px] leading-none">
                  {item.status}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
}))(PropertiesList)
